import React, { useState, useRef } from 'react';
import { switchLocale } from 'utils';
import { useClickOutside } from 'hooks';
import { trackCTAClick } from 'utils/analytics';
import { localePath } from 'utils/localePath';
import UserSwitcher from './UserSwitcher';
import LoginModal from './LoginModal';

// Which locales the site serves is a backend fact (config/supported_locales.rb).
// Deliberately no fallback list here: a duplicate would drift the moment a locale
// is added, which is the whole reason this component stopped holding one. If the
// payload is missing the menu renders empty, which is a visible bug rather than a
// silently stale set of choices.
const languageOptions = (translations) =>
  (translations.served_locales || []).map((code) => ({
    code,
    name: translations.languages?.[code] || code
  }));

const UnifiedHeader = ({ translations, userSignedIn, currentUserName, currentRole, userRoles, isMultiRole, isHomepage, unreadConversationsCount, hideFindTeachers }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');

  // Refs for click outside detection
  const languageDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Get CSRF token after component mounts
  React.useEffect(() => {
    const token = document.querySelector('[name="csrf-token"]')?.content;
    if (token) {
      setCsrfToken(token);
    }
  }, []);


  // 檢查是否為首頁
  const isHomepagePath = window.location.pathname === '/' || isHomepage;

  // 檢查是否為 teacher dashboard
  const isTeacherDashboard = window.location.pathname.startsWith('/dashboard');

  // 訊息頁面路徑（依角色）
  const conversationsPath = currentRole === 'student'
    ? '/student/dashboard/conversations'
    : '/dashboard/conversations';

  // 點擊外部關閉 dropdown
  useClickOutside(languageDropdownRef, () => setIsLanguageDropdownOpen(false), isLanguageDropdownOpen);
  useClickOutside(userDropdownRef, () => setIsUserDropdownOpen(false), isUserDropdownOpen);

  return (
    <>
    <header className="bg-white shadow-md relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center space-x-2 md:space-x-8">
            <h1 className="text-xl font-bold shrink-0">
              <a href={localePath("/")} className="group inline-flex items-center">
                {/* Mobile: T icon */}
                <svg className="md:hidden w-8 h-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#4169E1'}} />
                      <stop offset="50%" style={{stopColor: '#6B5DD3'}} />
                      <stop offset="100%" style={{stopColor: '#8B5CF6'}} />
                    </linearGradient>
                  </defs>
                  <rect width="32" height="32" rx="6" fill="url(#logo-gradient)" />
                  <g fill="white">
                    <rect x="5" y="7" width="22" height="4" rx="2" />
                    <rect x="14" y="7" width="4" height="18" rx="2" />
                  </g>
                </svg>
                {/* Desktop: Full text */}
                <span className="hidden md:inline bg-gradient-to-r from-[#6699FF] to-[#3366FF] bg-clip-text text-transparent group-hover:brightness-110 transition-all duration-200">
                  Tutor
                </span>
                <span className="hidden md:inline bg-gradient-to-r from-[#3366FF] to-[#2952CC] bg-clip-text text-transparent group-hover:brightness-110 transition-all duration-200">
                  Networking
                </span>
              </a>
            </h1>
            {/* Navigation Links - visible on all sizes */}
            {/* Hidden on a team's own page for referred / team-owned students */}
            <nav className="flex items-center space-x-2 md:space-x-6">
              {!hideFindTeachers && (
                <a
                  href={localePath("/find-teacher")}
                  className="text-xs md:text-sm font-medium text-gray-600 hover:text-[#3366FF] transition-colors duration-200 whitespace-nowrap"
                >
                  <span className="md:hidden">{translations.nav_find_teachers_short || 'Recommend'}</span>
                  <span className="hidden md:inline">{translations.nav_find_teachers || 'Recommend a teacher'}</span>
                </a>
              )}
            </nav>
            </div>

          {/* Center spacer */}
          <div className="flex-1"></div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {/* Language Switcher - 地球符號 */}
            <div ref={languageDropdownRef} className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center justify-center min-w-11 min-h-11 text-gray-700 hover:text-[#3366FF] text-2xl rounded-md transition-colors duration-200"
                title={translations.nav_select_language || 'Select Language'}
              >
                🌐
              </button>

              {/* Language Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-[100]">
                  <div className="py-1">
                    {languageOptions(translations).map(({ code, name }) => (
                      <button
                        key={code}
                        onClick={() => switchLocale(code)}
                        className="w-full text-left block px-4 py-3 min-h-11 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Admin User Switcher - only show for admin users */}
            {translations.actual_user_is_admin && <UserSwitcher translations={translations} />}

            {/* Unread Messages Icon - Desktop */}
            {userSignedIn && (
              <a
                href={conversationsPath}
                className="relative overflow-visible hidden md:flex items-center text-gray-700 hover:text-[#3366FF] px-2 py-1 rounded-md transition-colors duration-200"
                title={translations.nav_messages || 'Messages'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                {unreadConversationsCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold text-white bg-red-500">
                    {unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}
                  </span>
                )}
              </a>
            )}

            {/* Dashboard Link - Desktop (directly visible) */}
            {userSignedIn && currentRole === 'teacher' && (
              <a
                // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
                href="/dashboard"
                className="hidden md:flex items-center text-sm font-medium text-gray-600 hover:text-[#3366FF] transition-colors duration-200"
              >
                👨‍🏫 {translations.common?.teacher_dashboard || 'Teacher Dashboard'}
              </a>
            )}
            {userSignedIn && currentRole === 'student' && (
              <a
                // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
                href="/student/dashboard"
                className="hidden md:flex items-center text-sm font-medium text-gray-600 hover:text-[#3366FF] transition-colors duration-200"
              >
                📚 {translations.common?.student_dashboard || 'Student Dashboard'}
              </a>
            )}

            {userSignedIn ? (
              // 登入用戶：用戶名稱下拉選單
              <div ref={userDropdownRef} className="relative hidden md:block">
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center text-gray-700 hover:text-[#3366FF] text-sm px-3 py-2 rounded-md transition-colors duration-200"
                >
                  <span>{currentUserName}</span>
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-[100]">
                    <div className="py-1">
                      {/* Logout */}
                      <form action="/users/sign_out" method="post" className="block">
                        <input type="hidden" name="_method" value="delete" />
                        <input type="hidden" name="authenticity_token" value={csrfToken} />
                        <button
                          type="submit"
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {translations.common?.logout || 'Logout'}
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // 未登入用戶：點擊開啟角色選擇 Modal
              <div className="hidden md:block">
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#3366FF] hover:bg-[#2952CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3366FF]"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {translations.nav_teacher_login || 'Sign Up / Login'}
                </button>
              </div>
            )}

            {/* Unread Messages Icon - Mobile top bar */}
            {userSignedIn && (
              <a
                href={conversationsPath}
                className="relative overflow-visible md:hidden flex items-center text-gray-700 hover:text-[#3366FF] px-1 py-1"
                title={translations.nav_messages || 'Messages'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                {unreadConversationsCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-0.5 rounded-full text-[10px] font-bold text-white bg-red-500">
                    {unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}
                  </span>
                )}
              </a>
            )}

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#3366FF]"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {!isMobileMenuOpen ? (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              {/* 語言切換 - 手機版 */}
              <div className="block px-3 py-2">
                <div className="text-sm text-gray-600 mb-2">🌐 {translations.nav_select_language || 'Select Language'}</div>
                <div className="space-y-1">
                  {languageOptions(translations).map(({ code, name }) => (
                    <button
                      key={code}
                      onClick={() => switchLocale(code)}
                      className="w-full text-left block px-4 py-3 min-h-11 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>


              {userSignedIn ? (
                // 登入用戶的選項
                <>
                  {userRoles?.includes('teacher') && (
                    <a
                      // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
                      href="/dashboard"
                      className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#3366FF] hover:bg-gray-50">
                      👨‍🏫 {translations.common?.teacher_dashboard || 'Teacher Dashboard'}
                    </a>
                  )}
                  {userRoles?.includes('student') && (
                    <a
                      // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
                      href="/student/dashboard"
                      className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#3366FF] hover:bg-gray-50">
                      📚 {translations.common?.student_dashboard || 'Student Dashboard'}
                    </a>
                  )}

                  {/* Mobile Logout */}
                  <form action="/users/sign_out" method="post" className="block">
                    <input type="hidden" name="_method" value="delete" />
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button
                      type="submit"
                      className="w-full text-left block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#3366FF] hover:bg-gray-50"
                    >
                      {translations.common?.logout || 'Logout'}
                    </button>
                  </form>
                </>
              ) : (
                // 未登入用戶：點擊開啟角色選擇 Modal
                <div className="px-3 py-2">
                  <button
                    onClick={() => { setIsLoginModalOpen(true); setIsMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#3366FF] hover:bg-[#2952CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3366FF]"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {translations.nav_teacher_login || 'Sign Up / Login'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>

    {/* 角色選擇 Modal */}
    <LoginModal
      isOpen={isLoginModalOpen}
      onClose={() => setIsLoginModalOpen(false)}
      translations={translations}
    />
    </>
  );
};

export default UnifiedHeader;
